@if (config.templateRef; as template) {
  <ng-container *ngTemplateOutlet="template"></ng-container>
} @else if (groups() && !config.loading()) {
  <ul
    class="container"
    [class.multiple]="config.multiple"
    [class.optional]="config.optional || config.multiple"
  >
    <div class="header">
      @if (config.search) {
        <onyx-search
          data-testid="dropdown-search"
          class="search"
          [(query)]="config.query"
          [placeholder]="config.searchPlaceholder ?? null"
          color="white"
          [showFocus]="false"
          [loading]="loading() || config.searching()"
          [size]="32"
          (keydown.arrowup)="focusElement(); $event.preventDefault()"
        ></onyx-search>

        @if (!config.multiple) {
          <div class="separator"></div>
        }
      }

      @if (!isEmpty() && config.multiple && config.showSelectAll) {
        <button
          class="item toggle-all size-{{ config.optionSize }}"
          (click)="toggleAll()"
        >
          <span>
            {{
              `${I18N}.${selectedAll() ? 'deselectAll' : 'selectAll'}`
                | translate
            }}
          </span>

          @if (toggleAllLoading()) {
            <onyx-spinner color="black" [size]="16"></onyx-spinner>
          }
        </button>

        <div class="separator"></div>
      }
    </div>

    <div class="options" (scroll)="onScroll($event)">
      @if (!isEmpty()) {
        @for (
          group of groups();
          track group.id;
          let firstGroup = $first;
          let lastGroup = $last
        ) {
          <div class="group" #groupRef>
            @if (!firstGroup) {
              <div class="separator"></div>
            }

            @if (group.subheadingTemplateRef) {
              <ng-container
                *ngTemplateOutlet="group.subheadingTemplateRef"
              ></ng-container>
            } @else if (group.subheading) {
              <p class="subheading f-body-s">
                {{ group.subheading | translate }}
              </p>
            }

            @for (option of group.options; track option.value) {
              <button
                class="item size-{{ config.optionSize }}"
                [class.disabled]="option.disabled"
                [class.selected]="option._selected"
                [onyxTooltip]="option.tooltip"
                [onyxTooltipPositions]="
                  option.tooltipPosition ?? [OnyxOverlayPosition.RIGHT]
                "
                [onyxTooltipArrowOffsets]="{ y: -4 }"
                (click)="!option.disabled ? toggleOption(option.value) : null"
              >
                @if (option.spinner) {
                  <onyx-spinner
                    class="icon"
                    color="black"
                    [size]="option.spinnerSize ?? 20"
                  ></onyx-spinner>
                } @else if (option.leftIcon; as leftIcon) {
                  <onyx-icon
                    class="icon"
                    [class]="option.leftIconColor ?? ''"
                    [name]="leftIcon.name"
                    [size]="leftIcon.size"
                    [frame]="16"
                  ></onyx-icon>
                } @else if (option.leftEmoji; as emoji) {
                  <onyx-emoji [name]="emoji"></onyx-emoji>
                } @else if (option.leftFlag; as flag) {
                  <onyx-flag [countryCode]="flag" [size]="16"></onyx-flag>
                } @else if (option.leftDotColor; as color) {
                  <div class="dot {{ color }}"></div>
                } @else if (option.leftSquareColor; as color) {
                  <div class="square {{ color }}"></div>
                } @else if (option.avatar; as avatar) {
                  <onyx-avatar
                    variant="user"
                    [user]="avatar"
                    [size]="24"
                  ></onyx-avatar>
                }

                <ng-container
                  *ngTemplateOutlet="
                    config.optionTemplateRef ?? optionTemplate;
                    context: {
                      option,
                      query: config.query(),
                      disabled: option.disabled,
                      selected: option._selected,
                    }
                  "
                ></ng-container>

                @if (option.hint) {
                  <span class="alt f-body-s">{{ option.hint }}</span>
                }

                @if (option?.keys?.length && option?.keys; as keys) {
                  <onyx-shortcuts [keys]="keys" color="white"></onyx-shortcuts>
                }

                @if (
                  option?.rightButtons?.length && option?.rightButtons;
                  as rightButtons
                ) {
                  @for (button of rightButtons; track button) {
                    <onyx-icon-button
                      variant="transparent"
                      color="black"
                      [size]="24"
                      [onyxTooltip]="button.tooltip ?? undefined"
                      (click)="button.callback(); $event.stopPropagation()"
                    >
                      <onyx-icon [name]="button.icon" [size]="16"></onyx-icon>
                    </onyx-icon-button>
                  }
                }

                @if (option.rightIcon; as icon) {
                  <onyx-icon
                    class="icon"
                    [class]="option.rightIconColor ?? ''"
                    [class.hidden]="option._selected"
                    [name]="icon.name"
                    [size]="icon.size"
                    [frame]="16"
                    [onyxTooltip]="option.rightIconTooltip"
                    onyxTooltipTextAlign="left"
                  ></onyx-icon>
                } @else if (option.rightEmoji; as emoji) {
                  <onyx-emoji [name]="emoji"></onyx-emoji>
                } @else if (option.rightFlag; as flag) {
                  <onyx-flag [countryCode]="flag" [size]="16"></onyx-flag>
                }

                <onyx-icon
                  class="check"
                  [class.hidden]="!option._selected"
                  name="check-outlined"
                  [size]="16"
                  [frame]="16"
                ></onyx-icon>
              </button>
            }
          </div>
        }
      } @else {
        @if (config.emptyTemplateRef; as emptyTemplateRef) {
          <div class="no-options">
            @if (config.subheading; as subheading) {
              <p class="subheading f-body-s">
                {{ subheading | translate }}
              </p>
            }

            <ng-container *ngTemplateOutlet="emptyTemplateRef"></ng-container>
          </div>
        } @else if (config.query() && !config.searching()) {
          <div
            class="item size-{{ config.optionSize }} empty f-body-m f-italic"
          >
            {{ config.notFoundMessage ?? `${I18N}.notFound` | translate }}
          </div>
        }
      }
    </div>

    <div class="footer">
      @if (config.showAddOption) {
        @let query = config.query();
        @if (query || config.showAddOption !== 'value') {
          @if (!isEmpty()) {
            <div class="separator"></div>
          }

          @let disabled = config.addOptionDisabled;
          <button
            class="add item size-{{ config.optionSize }}"
            [class.disabled]="disabled"
            [disabled]="disabled"
            [onyxTooltip]="config.addOptionTooltip"
            [onyxTooltipPositions]="[OnyxOverlayPosition.RIGHT]"
            [onyxTooltipArrowOffsets]="{ y: -4 }"
            (click)="
              !disabled ? config.addOption?.(query) : null; config.close()
            "
          >
            {{ config.addOptionMessage ?? `${I18N}.add` | translate }}
            @if (query && config.addOptionShowQuery) {
              "{{ query }}"
            }
          </button>
        }
      }

      @if ((config.multiple || config.optional) && selectedAny()) {
        <div class="separator"></div>

        <button
          class="clear item size-{{ config.optionSize }}"
          (click)="clear()"
        >
          <onyx-icon name="close-outlined" [size]="20" [frame]="16"></onyx-icon>

          <span>{{ `${I18N}.clear` | translate }}</span>
        </button>
      }
    </div>
  </ul>
} @else {
  <onyx-spinner color="black"></onyx-spinner>
}

<ng-template let-option="option" let-query="query" #optionTemplate>
  <div class="content">
    <p class="name">
      <span [innerHTML]="option.name | translate | onyxHighlight: query"></span>

      @if (option.count != null) {
        <span class="alt f-body-s f-medium">{{ option.count | number }}</span>
      }
    </p>

    @if (option.description) {
      <p
        class="alt f-body-s"
        [innerHTML]="option.description | onyxHighlight: query"
      ></p>
    }
  </div>
</ng-template>
